import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalKey = 'privacy' | 'terms' | null;
const PRIVACY_CONTENT = <>
    <p data-id="element-10">
      En GUT LABS™ nos tomamos muy en serio la protección de tus datos
      personales. Esta Política de Privacidad explica qué información
      recopilamos, con qué finalidad y cómo puedes ejercer tus derechos en
      cumplimiento del Reglamento (UE) 2016/679 (RGPD) y la LOPDGDD.
    </p>
    <h4 data-id="element-11">1. Responsable del Tratamiento</h4>
    <p data-id="element-12">
      <strong data-id="element-13">Responsable:</strong> GUT LABS™
      <br data-id="element-14" />
      <strong data-id="element-15">Finalidad del tratamiento:</strong> Gestión de la compra, entrega
      y soporte del Protocolo Biome-Reset™, así como envío de comunicaciones
      relacionadas con tu cuenta y el producto adquirido.
    </p>
    <h4 data-id="element-16">2. Datos que Recopilamos</h4>
    <p data-id="element-17">
      Recopilamos únicamente los datos estrictamente necesarios: nombre, correo
      electrónico, datos de facturación y de pago. Los datos de pago se procesan
      a través de pasarelas seguras certificadas (SSL 256-bit) y no se almacenan
      en nuestros servidores.
    </p>
    <h4 data-id="element-18">3. Base Legal</h4>
    <p data-id="element-19">
      La base legal para el tratamiento de tus datos es la ejecución del
      contrato de compraventa del producto digital y, en su caso, tu
      consentimiento expreso para comunicaciones comerciales.
    </p>
    <h4 data-id="element-20">4. Conservación de los Datos</h4>
    <p data-id="element-21">
      Conservaremos tus datos durante el tiempo necesario para cumplir con la
      finalidad para la que fueron recogidos y para cumplir con las obligaciones
      legales de carácter fiscal y mercantil.
    </p>
    <h4 data-id="element-22">5. Tus Derechos</h4>
    <p data-id="element-23">
      Tienes derecho a acceder, rectificar, suprimir, oponerte, limitar el
      tratamiento y solicitar la portabilidad de tus datos. Para ejercer sus
      derechos o realizar cualquier consulta, el usuario puede contactar a
      través de nuestro Centro de Soporte en la plataforma de alumnos.
    </p>
    <h4 data-id="element-24">6. Cookies</h4>
    <p data-id="element-25">
      Este sitio utiliza cookies técnicas y, con tu consentimiento, cookies
      analíticas y publicitarias. Puedes gestionar tus preferencias en cualquier
      momento.
    </p>
  </>;
const TERMS_CONTENT = <>
    <p data-id="element-26">
      Las presentes Condiciones del Servicio regulan el acceso, la compra y el
      uso del Protocolo Biome-Reset™ ofrecido por GUT LABS™.
    </p>
    <h4 data-id="element-27">1. Objeto</h4>
    <p data-id="element-28">
      El Protocolo Biome-Reset™ es un programa digital educativo de salud
      digestiva. La adquisición del producto otorga acceso de por vida al
      contenido descargable y a sus actualizaciones.
    </p>
    <h4 data-id="element-29">2. Precio y Pago</h4>
    <p data-id="element-30">
      El precio aplicable es el indicado en la página de oferta en el momento de
      la compra. Se trata de un pago único, sin renovaciones automáticas ni
      suscripciones recurrentes. El pago se procesa mediante pasarelas seguras.
    </p>
    <h4 data-id="element-31">3. Derecho de Desistimiento y Garantía</h4>
    <p data-id="element-32">
      Al tratarse de contenido digital de descarga inmediata, el cliente
      reconoce expresamente la pérdida del derecho de desistimiento de 14 días
      una vez accedido al material (art. 103 m. del Real Decreto Legislativo
      1/2007). No obstante, GUT LABS™ ofrece una garantía comercial voluntaria
      de devolución de 30 días si el cliente no queda satisfecho. Para ejercer
      sus derechos o realizar cualquier consulta, el usuario puede contactar a
      través de nuestro Centro de Soporte en la plataforma de alumnos.
    </p>
    <h4 data-id="element-33">4. Uso del Contenido</h4>
    <p data-id="element-34">
      El contenido es de uso personal e intransferible. Queda prohibida su
      reproducción, distribución, comunicación pública o transformación sin
      autorización expresa por escrito del titular.
    </p>
    <h4 data-id="element-35">5. Descargo Médico</h4>
    <p data-id="element-36">
      El Protocolo Biome-Reset™ es un programa educativo. La información no
      constituye consejo médico ni diagnóstico. Consulta con un profesional
      antes de realizar cambios en tu dieta, especialmente ante patologías,
      embarazo o lactancia.
    </p>
    <h4 data-id="element-37">6. Limitación de Responsabilidad</h4>
    <p data-id="element-38">
      GUT LABS™ no garantiza resultados específicos. El uso de la información
      es bajo la responsabilidad exclusiva del usuario.
    </p>
    <h4 data-id="element-39">7. Legislación Aplicable</h4>
    <p data-id="element-40">
      Estas condiciones se rigen por la legislación española. Para cualquier
      controversia, las partes se someten a los Juzgados y Tribunales del
      domicilio del consumidor.
    </p>
  </>;
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-41">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-forest/40 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-42">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-43">
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-sage" data-id="element-44">
              <h3 id="legal-modal-title" className="text-lg md:text-xl font-bold tracking-tight text-forest" data-id="element-45">
                {title}
              </h3>
              <button onClick={onClose} aria-label="Cerrar" className="w-9 h-9 rounded-full hover:bg-sage flex items-center justify-center text-forest/60 hover:text-forest transition-colors" data-id="element-46">
                <X className="w-5 h-5" data-id="element-47" />
              </button>
            </div>
            <div className="overflow-y-auto px-6 md:px-8 py-6 text-sm leading-relaxed text-forest/75 [&_h4]:text-forest [&_h4]:font-bold [&_h4]:text-base [&_h4]:mt-6 [&_h4]:mb-2 [&_p]:mb-3 [&_strong]:text-forest [&_strong]:font-semibold" data-id="element-48">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalKey>(null);
  return <>
      <footer className="bg-cream-warm border-t border-blush/50" data-id="element-49">
        <div className="container mx-auto px-6 max-w-4xl py-16 text-center" data-id="element-50">
          {/* Wordmark */}
          <div className="font-serif text-2xl font-semibold tracking-tight text-forest mb-8" data-id="element-51">
            GUT LABS
            <sup className="text-[0.55em] font-medium ml-0.5 tracking-tight" data-id="element-52">
              TM
            </sup>
          </div>

          {/* Legal Links */}
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-8" data-id="element-53">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-forest/80 hover:text-forest transition-colors font-medium" data-id="element-54">
              Política de Privacidad
            </button>
            <span className="text-forest/20 hidden sm:inline" data-id="element-55">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-forest/80 hover:text-forest transition-colors font-medium" data-id="element-56">
              Condiciones del Servicio
            </button>
          </nav>

          {/* Medical Disclaimer — visible */}
          <div className="bg-white border border-sage-dark/50 rounded-lg px-5 py-4 max-w-2xl mx-auto mb-8" data-id="element-57">
            <p className="text-xs leading-relaxed text-forest/70 font-medium" data-id="element-58">
              <span className="font-bold text-forest tracking-wider" data-id="element-59">
                IMPORTANTE:
              </span>{' '}
              Este programa es de carácter informativo y educativo. No sustituye
              el diagnóstico o tratamiento de un profesional de la salud.
            </p>
          </div>

          {/* Meta Disclaimer */}
          <p className="text-[11px] leading-relaxed text-forest/40 font-medium max-w-2xl mx-auto mb-6" data-id="element-60">
            Este sitio no forma parte del sitio web de Facebook o de Meta
            Platforms Inc. Además, este sitio NO está respaldado por Facebook de
            ninguna manera. Facebook es una marca comercial de Meta Platforms
            Inc.
          </p>

          {/* Copyright */}
          <p className="text-xs text-forest/45 font-medium" data-id="element-61">
            © 2024 GUT LABS™. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-62">
        {PRIVACY_CONTENT}
      </LegalModal>

      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Condiciones del Servicio" data-id="element-63">
        {TERMS_CONTENT}
      </LegalModal>
    </>;
}